import { type ChangeEvent, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider, Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  PuiTextField,
  useFields,
  type ValidateOptions,
} from '@pbt/pbt-ui-components'

import PuiSwitch from '~/components/common/PuiSwitch'
import { usePracticeFieldsSection } from '~/components/dashboard/admin/general/practices/hooks/usePracticeFieldsSection'
import type { HeskaLabCredentials, LabProps } from '~/types'

import { PracticeDetailsPanels } from '../../../practices'
import VendorFormContainer from '../VendorFormContainer'

const useStyles = makeStyles(
  (theme) => ({
    field: {
      ...theme.constants.resetPasswordStyle,
    },
  }),
  { name: 'HeskaLab' },
)

const HeskaLab = ({
  business,
  initiallyActive,
  name,
  credentials,
  status,
  vendorId,
  isTesting,
  onToggle,
  onTest,
}: LabProps<HeskaLabCredentials>) => {
  const classes = useStyles()
  const { t } = useTranslation('Common')

  const { fields, validate, reset } = useFields(
    [
      {
        name: 'active',
        label: name,
        type: 'toggle',
        initialValue: initiallyActive || false,
      },
      {
        name: 'username',
        label: t('Common:USERNAME'),
        validators: ['required'],
        initialValue: credentials.username || '',
      },
      {
        name: 'password',
        label: t('Common:PASSWORD'),
        initialValue: credentials.password || '',
      },
    ],
    false,
  )

  const { active, username, password } = fields

  usePracticeFieldsSection({
    business,
    fields,
    sectionName:
      `${PracticeDetailsPanels.LAB_INTEGRATIONS}_HESKA` as PracticeDetailsPanels,
    parentSectionName: PracticeDetailsPanels.LAB_INTEGRATIONS,
    validate: (options?: ValidateOptions | undefined): boolean => {
      if (active.value) {
        return validate(options)
      }
      return true
    },
    reset,
    resetDependencies: [initiallyActive, business],
  })

  return (
    <>
      <PuiSwitch
        field={{
          ...active,
          set: (event: ChangeEvent<HTMLInputElement>) =>
            onToggle(event.target?.checked, () => active.set(event)),
        }}
        label={active.label}
      />
      {active?.value && (
        <>
          <Grid container direction="column" pb={3} pl={6}>
            <VendorFormContainer
              isTesting={isTesting}
              status={status}
              onTest={() =>
                onTest({
                  active: active.value,
                  password: password.value,
                  username: username.value,
                  vendorId,
                })
              }
            >
              <Grid item md={6} px={1.5} py={1}>
                <PuiTextField
                  autoComplete="off"
                  field={username}
                  inputProps={{ maxLength: 100 }}
                  label={username.label}
                />
              </Grid>
              <Grid item md={6} px={1.5} py={1}>
                <PuiTextField
                  shrink
                  autoComplete="off"
                  field={password}
                  inputProps={{ className: classes.field, maxLength: 100 }}
                  label={password.label}
                  placeholder={
                    active.value
                      ? `<${t('Common:USE_STORED_PASSWORD').toLowerCase()}>`
                      : '∗∗∗∗∗∗∗∗∗∗∗∗∗'
                  }
                  type="password"
                />
              </Grid>
            </VendorFormContainer>
          </Grid>
          <Divider />
        </>
      )}
    </>
  )
}

export default memo(HeskaLab, (prevProps, nextProps) =>
  R.equals(prevProps, nextProps),
)
